/**
 * Lista de campos de reserva: permite añadir/eliminar líneas de reserva.
 * Cada campo incluye un botón de cámara para escanear QR / códigos de barras.
 */
import { Plus, X, ScanLine } from 'lucide-react';
import styles from './ReservaFieldList.module.css';

interface ReservaFieldListProps {
    codigoReservas: string[];
    onReservaChange: (index: number, text: string) => void;
    onRemoveReservaField: (index: number) => void;
    onAddReservaField: () => void;
    /** Abre el escáner de cámara para el campo indicado. */
    onCameraPress: (index: number) => void;
    /** Notas libres del formulario (varias líneas). */
    anotaciones: string;
    onAnotacionesChange: (text: string) => void;
}

export default function ReservaFieldList({
    codigoReservas,
    onReservaChange,
    onRemoveReservaField,
    onAddReservaField,
    onCameraPress,
    anotaciones,
    onAnotacionesChange,
}: ReservaFieldListProps) {
    const canRemoveRow = codigoReservas.length > 1;

    return (
        <div className={styles.fieldContainer}>
            {codigoReservas.map((reserva, idx) => (
                <div key={idx} className={styles.inputGroupContainer}>
                    <input
                        className={styles.textInputFlex}
                        placeholder={`Reserva ${idx + 1}`}
                        value={reserva}
                        onChange={e => onReservaChange(idx, e.target.value)}
                    />

                    {reserva.length > 0 && (
                        <button
                            className={styles.clearIcon}
                            onClick={() => onReservaChange(idx, '')}
                            type="button"
                            aria-label={`Borrar texto reserva ${idx + 1}`}
                        >
                            <X size={16} color="currentColor" strokeWidth={2.25} aria-hidden />
                        </button>
                    )}

                    <button
                        className={styles.scanIcon}
                        onClick={() => onCameraPress(idx)}
                        type="button"
                        aria-label={`Escanear código para reserva ${idx + 1}`}
                    >
                        <ScanLine size={18} color="currentColor" strokeWidth={2.25} aria-hidden />
                    </button>
                </div>
            ))}

            <div className={styles.addReservaRow}>
                <button
                    type="button"
                    className={styles.actionIcon}
                    onClick={onAddReservaField}
                    aria-label="Añadir otra reserva"
                >
                    <Plus size={18} color="currentColor" strokeWidth={2.25} aria-hidden />
                </button>
                {canRemoveRow && (
                    <button
                        type="button"
                        className={styles.actionIcon}
                        onClick={() => onRemoveReservaField(codigoReservas.length - 1)}
                        aria-label="Eliminar última reserva"
                    >
                        <X size={18} color="currentColor" strokeWidth={2.25} aria-hidden />
                    </button>
                )}
            </div>

            <div className={styles.annotacionesBox}>
                <textarea
                    className={styles.textAreaAnotaciones}
                    placeholder="Anotaciones (opcional)"
                    value={anotaciones}
                    onChange={e => onAnotacionesChange(e.target.value)}
                    aria-label="Anotaciones del formulario"
                    maxLength={2000}
                    rows={3}
                    spellCheck
                />
            </div>
        </div>
    );
}
